import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Calendar,
  CreditCard,
  Loader2,
  CheckCircle,
  XCircle,
  QrCode,
  Zap,
  X
} from 'lucide-react';
import { cn } from '@/lib/core/utils';
import { PaymentItem, PaymentStageId, StageStats } from './payments/types';

interface PaymentProgressSummaryProps {
  payments: PaymentItem[];
  selectedStage: PaymentStageId | null;
  onStageSelect: (stage: PaymentStageId | null) => void;
  onBulkExportQR: () => void;
  onProcessAllReady?: () => void;
  formatCurrency: (amount: number) => string;
  stripeEnabled: boolean;
  disabled: boolean;
  isProcessing: boolean;
}

const STAGE_CONFIG: Record<PaymentStageId, {
  label: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  borderColor: string;
}> = {
  upcoming: {
    label: 'Upcoming',
    icon: Calendar,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/30',
    borderColor: 'border-blue-200 dark:border-blue-800',
  },
  ready: {
    label: 'Ready',
    icon: CreditCard,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-900/30',
    borderColor: 'border-orange-200 dark:border-orange-800',
  },
  processing: {
    label: 'Processing',
    icon: Loader2,
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/30',
    borderColor: 'border-yellow-200 dark:border-yellow-800',
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/30',
    borderColor: 'border-green-200 dark:border-green-800',
  },
  failed: {
    label: 'Failed',
    icon: XCircle,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/30',
    borderColor: 'border-red-200 dark:border-red-800',
  },
};

const STAGE_ORDER: PaymentStageId[] = ['upcoming', 'ready', 'processing', 'completed', 'failed'];

export const PaymentProgressSummary: React.FC<PaymentProgressSummaryProps> = ({
  payments,
  selectedStage,
  onStageSelect,
  onBulkExportQR,
  onProcessAllReady,
  formatCurrency,
  stripeEnabled,
  disabled,
  isProcessing,
}) => {
  // Calculate stats for each stage
  const statsByStage = useMemo(() => {
    const stats: Record<PaymentStageId, StageStats> = {
      upcoming: { count: 0, totalAmount: 0 },
      ready: { count: 0, totalAmount: 0 },
      processing: { count: 0, totalAmount: 0 },
      completed: { count: 0, totalAmount: 0 },
      failed: { count: 0, totalAmount: 0 },
    };

    payments.forEach(payment => {
      if (stats[payment.stage]) {
        stats[payment.stage].count++;
        stats[payment.stage].totalAmount += payment.amount;
      }
    });

    return stats;
  }, [payments]);

  const readyCount = statsByStage.ready.count;
  const failedCount = statsByStage.failed.count;
  const hasReadyPayments = readyCount > 0;

  // Don't render if no payments at all
  if (payments.length === 0) {
    return null;
  }

  return null;
};

export default PaymentProgressSummary;
